import React, { useMemo } from 'react';
import { Column } from 'react-table';
import { Button } from '../Button/Button';
import { SvgIcon } from '../SvgIcons/svgIcons';
import { StyledHistoryTable, HistoryBadge } from './VersionHistoryTable.styles';
import { IVersion, VersionHistoryTableLabels } from './types';

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  dateStyle: 'short',
  timeStyle: 'short',
};

const dateFormatter = new Intl.DateTimeFormat('en-GB', DATE_FORMAT_OPTIONS);

export interface VersionHistoryTableProps {
  versions: IVersion[];
  selectedVersionId?: string;
  currentVersionId?: string;
  onPreview: (version: IVersion) => void;
  onRestore?: (version: IVersion) => void;
  labels: VersionHistoryTableLabels;
}

export const VersionHistoryTable: React.FC<VersionHistoryTableProps> = ({
  versions,
  selectedVersionId = '',
  currentVersionId,
  onPreview,
  onRestore,
  labels,
}) => {
  const resolvedCurrentVersionId = currentVersionId ?? versions[0]?.id;

  const columns: Column<IVersion>[] = useMemo(
    () => [
      {
        Header: labels.lastUpdated,
        accessor: 'date',
        Cell: (row: { original: IVersion; index: number }) => {
          const version = row.original;
          const formattedDate = dateFormatter.format(new Date(version.date));
          const isCurrentVersion = version.id === resolvedCurrentVersionId;
          return (
            <div title={`Version ID: ${version.id}`}>
              {formattedDate}
              {isCurrentVersion && (
                <>
                  <br />
                  <HistoryBadge>{labels.currentVersionBadge}</HistoryBadge>
                </>
              )}
            </div>
          );
        },
        width: 180,
        resizable: false,
      },
      {
        Header: labels.updatedBy,
        Cell: (row: { original: IVersion }) => {
          const version = row.original;
          const title =
            version.authorEmail !== undefined || version.authorId !== undefined
              ? `Email: ${version.authorEmail ?? ''}, ID: ${version.authorId ?? ''}`
              : undefined;
          return <span title={title}>{version.authorName}</span>;
        },
        width: 150,
      },
      {
        Header: labels.comment,
        accessor: 'comment',
        style: { whiteSpace: 'unset' },
      },
      {
        Header: labels.preview,
        headerClassName: 'th-center',
        headerStyle: { textAlign: 'center' },
        style: { textAlign: 'center' },
        Cell: (row: { original: IVersion }) => {
          const version = row.original;
          const isSelected =
            (selectedVersionId !== '' && selectedVersionId === version.id) ||
            (selectedVersionId === '' && resolvedCurrentVersionId === version.id);

          if (isSelected) {
            return labels.selected;
          }

          return (
            <>
              <Button
                id={`btn-preview-${version.id}`}
                rightSideIcon={<SvgIcon name="arrow-right" />}
                variation="secondary"
                onClick={() => onPreview(version)}
              >
                {labels.preview}
              </Button>
              {onRestore && labels.restore && (
                <Button
                  id={`btn-restore-${version.id}`}
                  variation="secondary"
                  onClick={() => onRestore(version)}
                  data-testid={`restore-${version.id}`}
                >
                  {labels.restore}
                </Button>
              )}
            </>
          );
        },
        width: 160,
        resizable: false,
      },
    ],
    [labels, resolvedCurrentVersionId, selectedVersionId, onPreview, onRestore],
  );

  return (
    <StyledHistoryTable
      columns={columns}
      data={versions}
      defaultSorted={[{ id: 'date', desc: true }]}
      defaultPageSize={100}
      sortable={false}
    />
  );
};
